import {
  AutoDecidedFilter,
  LearningOutcome,
  type LearningOutcomeFields,
} from "@/components/agents/tabs/learning-outcome";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { ApiError, api } from "@/lib/api";
import { formatDate } from "@/lib/i18n";
import { Link, Navigate, createFileRoute, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * `/suggestions/learnings` — admin-only review queue for the learning loop.
 *
 * What Hubert's own mining job found in its approve/reject history (rejected
 * suggestions with a reason, approved suggestions the admin edited first) as
 * short corrective patterns. A pattern is `draft` (mined, inert) until a
 * human Activates it — only then does it reach Hubert's prompt on future
 * analyses. Dismiss and Activate both stay fully reversible, at any time:
 * flipping status is the whole effect, nothing else to undo.
 *
 * A reviewer can also escalate a pattern beyond a prompt note: "Send to
 * Metoděj" opens a work item asking the junior-dev agent to fix it in
 * Hub's own analyst code instead of (or alongside) activating it as a
 * note — for patterns a prompt tweak alone can't really fix. Independent
 * of Activate/Dismiss; a learning can be both active and converted.
 *
 * See `server/routes/analyst-learnings.ts` for the API and
 * `server/services/analyst-learning/mine.ts` for how a draft is produced.
 */

export const Route = createFileRoute("/_app/suggestions/learnings")({
  component: LearningsPage,
});

type Learning = LearningOutcomeFields & {
  id: string;
  text: string;
  status: "draft" | "active" | "dismissed" | "expired";
  evidenceCount: number;
  generatedAt: string;
  project: { id: string; name: string } | null;
  reviewedBy: { id: string; name: string | null } | null;
  reviewedAt: string | null;
  convertedWorkItemId: string | null;
};

const TABS = ["draft", "active", "expired", "dismissed", "all"] as const;
type Tab = (typeof TABS)[number];

function LearningsPage() {
  const { t: tr } = useTranslation();
  const { user } = useLoaderData({ from: "/_app" });
  const [tab, setTab] = useState<Tab>("draft");
  const [rows, setRows] = useState<Learning[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [mining, setMining] = useState(false);
  const [mineMessage, setMineMessage] = useState<string | null>(null);
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
  // Which project each card's "send to Metoděj" picker currently has
  // selected — per-row, since different learnings may target different
  // codebases (though in practice it's almost always Hub's own).
  const [convertProject, setConvertProject] = useState<Record<string, string>>({});

  const [autoOnly, setAutoOnly] = useState(false);

  const load = useCallback(
    (t: Tab) => {
      setRows(null);
      const query = new URLSearchParams({
        ...(t === "all" ? {} : { status: t }),
        ...(autoOnly ? { auto: "1" } : {}),
      }).toString();
      return api
        .get<{ learnings: Learning[] }>(`/api/analyst-learnings${query ? `?${query}` : ""}`)
        .then((res) => setRows(res.learnings))
        .catch(() => setError(tr("sug.learnings.loadError")));
    },
    [tr, autoOnly],
  );

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    void load(tab);
  }, [user.globalRole, tab, load]);

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    api
      .get<{ projects: Array<{ id: string; name: string }> }>("/api/projects?pageSize=200")
      .then((res) => setProjects(res.projects.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {
        /* the picker just stays empty — convert stays disabled */
      });
  }, [user.globalRole]);

  async function decide(id: string, status: "active" | "dismissed") {
    setBusyId(id);
    try {
      await api.patch(`/api/analyst-learnings/${id}`, { status });
      await load(tab);
    } catch {
      setError(tr("sug.learnings.saveError"));
    } finally {
      setBusyId(null);
    }
  }

  /** Escalate a mined pattern to a real code fix: opens a work item
   *  assigned to Metoděj in whichever project the reviewer picked
   *  (almost always Hub's own codebase — the fix is always in Hub's
   *  analyst code, never the customer project a scoped learning is about). */
  async function convertToTask(id: string) {
    const projectId = convertProject[id];
    if (!projectId) return;
    setBusyId(id);
    try {
      const res = await api.post<{ workItemId: string }>(
        `/api/analyst-learnings/${id}/convert-to-task`,
        { projectId },
      );
      setRows(
        (prev) =>
          prev?.map((r) => (r.id === id ? { ...r, convertedWorkItemId: res.workItemId } : r)) ??
          prev,
      );
    } catch {
      setError(tr("sug.learnings.convertError"));
    } finally {
      setBusyId(null);
    }
  }

  async function mineNow() {
    setMining(true);
    setMineMessage(null);
    try {
      await api.post("/api/analyst-learnings/mine", {});
      setMineMessage(tr("sug.learnings.mineStarted"));
    } catch (err) {
      setMineMessage(
        err instanceof ApiError && err.status === 503
          ? tr("sug.learnings.mineNoAi")
          : tr("sug.learnings.mineError"),
      );
    } finally {
      setMining(false);
    }
  }

  if (user.globalRole !== "admin") {
    return <Navigate to="/" />;
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{tr("sug.learnings.title")}</h1>
          <p className="text-sm text-muted-foreground">{tr("sug.learnings.intro")}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void mineNow()}
            disabled={mining}
          >
            {mining ? tr("sug.learnings.starting") : tr("sug.learnings.mineNow")}
          </Button>
          {mineMessage && <p className="text-xs text-muted-foreground">{mineMessage}</p>}
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`learnings-tab-${key}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {tr(`sug.learnings.tabs.${key}`)}
          </button>
        ))}
        <AutoDecidedFilter checked={autoOnly} onChange={setAutoOnly} />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {rows && rows.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="learnings-empty">
          {tab === "draft" ? tr("sug.learnings.emptyDraft") : tr("sug.learnings.empty")}
        </p>
      )}

      {rows?.map((r) => (
        <Card key={r.id} data-testid="learning-card">
          <CardContent className="space-y-2 pt-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="secondary">
                {r.project ? r.project.name : tr("sug.learnings.global")}
              </Badge>
              <span>{tr("sug.learnings.evidence", { count: r.evidenceCount })}</span>
              <span>{tr("sug.learnings.mined", { date: formatDate(r.generatedAt) })}</span>
              {r.reviewedBy && (
                <span>
                  {tr(
                    r.status === "active"
                      ? "sug.learnings.activatedBy"
                      : "sug.learnings.dismissedBy",
                    { name: r.reviewedBy.name },
                  )}
                </span>
              )}
            </div>
            <LearningOutcome learning={r} />
            <p className="text-sm">{r.text}</p>
            <div className="flex gap-2">
              {r.status !== "active" && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void decide(r.id, "active")}
                  disabled={busyId === r.id}
                  data-testid="activate-learning"
                >
                  {tr("sug.learnings.activate")}
                </Button>
              )}
              {r.status !== "dismissed" && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => void decide(r.id, "dismissed")}
                  disabled={busyId === r.id}
                  data-testid="dismiss-learning"
                >
                  {tr("sug.learnings.dismiss")}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2 border-t pt-2">
              {r.convertedWorkItemId ? (
                <span className="text-xs text-muted-foreground">
                  {tr("sug.learnings.sentTo")}
                  <Link
                    to="/work-items/$id"
                    params={{ id: r.convertedWorkItemId }}
                    className="underline"
                  >
                    {tr("sug.learnings.openTicket")}
                  </Link>
                </span>
              ) : (
                <>
                  <Select
                    size="sm"
                    aria-label={tr("sug.learnings.targetProject")}
                    value={convertProject[r.id] ?? ""}
                    onChange={(v) => setConvertProject((prev) => ({ ...prev, [r.id]: v }))}
                    options={[
                      { value: "", label: tr("sug.learnings.pickProject") },
                      ...projects.map((p) => ({ value: p.id, label: p.name })),
                    ]}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void convertToTask(r.id)}
                    disabled={busyId === r.id || !convertProject[r.id]}
                    data-testid="convert-to-task"
                  >
                    {tr("sug.learnings.sendToMetodej")}
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
